import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Globe, Search, Wifi, Network, Shield, FileText, MapPin,
  Save, Trash2, Loader2, CheckCircle2, XCircle, Clock,
  Copy, Server, Lock, AlertTriangle, Bookmark,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

// ── Types ─────────────────────────────────────────────────────────

interface DnsResult {
  domain: string
  recordType: string
  records: unknown[]
  queryTime: number
}

interface WhoisResult {
  domain: string
  raw: string
  parsed: Record<string, string>
  queryTime: number
}

interface PingResult {
  host: string
  resolved: string
  results: Array<{ seq: number; time: number; success: boolean }>
  stats: { sent: number; received: number; lost: number; avgTime: number; minTime: number; maxTime: number }
}

interface PortCheckResult {
  host: string
  port: number
  open: boolean
  responseTime: number
  error?: string
}

interface SubnetCalcResult {
  cidr: string
  networkAddress: string
  broadcastAddress: string
  subnetMask: string
  wildcardMask: string
  firstHost: string
  lastHost: string
  totalHosts: number
  usableHosts: number
  prefixLength: number
  ipClass: string
  isPrivate: boolean
  binaryMask: string
}

interface SslCheckResult {
  host: string
  port: number
  valid: boolean
  issuer: Record<string, string>
  subject: Record<string, string>
  validFrom: string
  validTo: string
  daysRemaining: number
  serialNumber: string
  fingerprint: string
  protocol: string
  bits: number
  sans: string[]
}

interface HeadersCheckResult {
  url: string
  statusCode: number
  headers: Record<string, string>
  securityScore: number
  findings: Array<{ header: string; status: 'present' | 'missing' | 'warning'; message: string }>
}

interface IpInfoResult {
  ip: string
  hostname?: string
  city?: string
  region?: string
  country?: string
  loc?: string
  org?: string
  postal?: string
  timezone?: string
}

interface SavedResult {
  id: string
  toolName: string
  input: Record<string, unknown>
  result: Record<string, unknown>
  label: string | null
  createdAt: string
}

// ── Helper ────────────────────────────────────────────────────────

function copyToClipboard(text: string, toast: ReturnType<typeof useToast>['toast']) {
  navigator.clipboard.writeText(text)
  toast({ title: 'Copied', description: 'Copied to clipboard' })
}

// ── DNS Lookup Tool ───────────────────────────────────────────────

function DnsLookupTool() {
  const [domain, setDomain] = useState('')
  const [recordType, setRecordType] = useState('A')
  const { toast } = useToast()

  const mutation = useMutation({
    mutationFn: (data: { domain: string; recordType: string }) =>
      apiClient.post<DnsResult>('/it-tools/dns-lookup', data),
    onError: () => toast({ title: 'Error', description: 'DNS lookup failed', variant: 'destructive' }),
  })

  const result = mutation.data?.data

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="flex-1">
          <Label>Domain</Label>
          <Input
            placeholder="example.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && domain && mutation.mutate({ domain, recordType })}
          />
        </div>
        <div className="w-32">
          <Label>Record Type</Label>
          <Select value={recordType} onValueChange={setRecordType}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {['A', 'AAAA', 'MX', 'TXT', 'NS', 'CNAME', 'SOA'].map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button
            onClick={() => mutation.mutate({ domain, recordType })}
            disabled={!domain || mutation.isPending}
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span className="ml-2">Lookup</span>
          </Button>
        </div>
      </div>

      {result && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{result.domain} - {result.recordType} Records</CardTitle>
              <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />{result.queryTime}ms</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {result.records.map((record, i) => (
                <div key={i} className="flex items-center justify-between bg-muted/50 rounded-md px-3 py-2 font-mono text-sm">
                  <span>{typeof record === 'object' ? JSON.stringify(record) : String(record)}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => copyToClipboard(typeof record === 'object' ? JSON.stringify(record) : String(record), toast)}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              {result.records.length === 0 && (
                <p className="text-sm text-muted-foreground">No records found</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ── WHOIS Tool ────────────────────────────────────────────────────

function WhoisTool() {
  const [domain, setDomain] = useState('')
  const { toast } = useToast()

  const mutation = useMutation({
    mutationFn: (data: { domain: string }) =>
      apiClient.post<WhoisResult>('/it-tools/whois', data),
    onError: () => toast({ title: 'Error', description: 'WHOIS lookup failed', variant: 'destructive' }),
  })

  const result = mutation.data?.data

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="flex-1">
          <Label>Domain</Label>
          <Input
            placeholder="example.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && domain && mutation.mutate({ domain })}
          />
        </div>
        <div className="flex items-end">
          <Button
            onClick={() => mutation.mutate({ domain })}
            disabled={!domain || mutation.isPending}
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span className="ml-2">Lookup</span>
          </Button>
        </div>
      </div>

      {result && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">WHOIS - {result.domain}</CardTitle>
              <Badge variant="outline"><Clock className="h-3 w-3 mr-1" />{result.queryTime}ms</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {Object.keys(result.parsed).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(result.parsed).slice(0, 20).map(([key, value]) => (
                  <div key={key} className="bg-muted/50 rounded-md px-3 py-2">
                    <p className="text-xs text-muted-foreground">{key}</p>
                    <p className="text-sm font-mono truncate">{value}</p>
                  </div>
                ))}
              </div>
            ) : (
              <Textarea
                readOnly
                value={result.raw}
                className="font-mono text-xs h-64"
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ── Ping Tool ─────────────────────────────────────────────────────

function PingTool() {
  const [host, setHost] = useState('')
  const [count, setCount] = useState('4')
  const { toast } = useToast()

  const mutation = useMutation({
    mutationFn: (data: { host: string; count: number }) =>
      apiClient.post<PingResult>('/it-tools/ping', data),
    onError: () => toast({ title: 'Error', description: 'Ping failed', variant: 'destructive' }),
  })

  const result = mutation.data?.data

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="flex-1">
          <Label>Host</Label>
          <Input
            placeholder="example.com or 8.8.8.8"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && host && mutation.mutate({ host, count: parseInt(count) })}
          />
        </div>
        <div className="w-24">
          <Label>Count</Label>
          <Input
            type="number"
            min={1}
            max={10}
            value={count}
            onChange={(e) => setCount(e.target.value)}
          />
        </div>
        <div className="flex items-end">
          <Button
            onClick={() => mutation.mutate({ host, count: parseInt(count) })}
            disabled={!host || mutation.isPending}
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wifi className="h-4 w-4" />}
            <span className="ml-2">Ping</span>
          </Button>
        </div>
      </div>

      {result && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Ping {result.host} ({result.resolved})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              {result.results.map((r) => (
                <div key={r.seq} className="flex items-center gap-2 font-mono text-sm">
                  {r.success ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                  <span>seq={r.seq} time={r.time}ms {!r.success && '(timeout)'}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-muted/50 rounded-md px-3 py-2 text-center">
                <p className="text-xs text-muted-foreground">Packets</p>
                <p className="text-lg font-bold">{result.stats.received}/{result.stats.sent}</p>
              </div>
              <div className="bg-muted/50 rounded-md px-3 py-2 text-center">
                <p className="text-xs text-muted-foreground">Avg Time</p>
                <p className="text-lg font-bold">{result.stats.avgTime}ms</p>
              </div>
              <div className="bg-muted/50 rounded-md px-3 py-2 text-center">
                <p className="text-xs text-muted-foreground">Loss</p>
                <p className="text-lg font-bold">{result.stats.sent > 0 ? Math.round((result.stats.lost / result.stats.sent) * 100) : 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ── Port Check Tool ───────────────────────────────────────────────

function PortCheckTool() {
  const [host, setHost] = useState('')
  const [port, setPort] = useState('443')
  const { toast } = useToast()

  const mutation = useMutation({
    mutationFn: (data: { host: string; port: number }) =>
      apiClient.post<PortCheckResult>('/it-tools/port-check', data),
    onError: () => toast({ title: 'Error', description: 'Port check failed', variant: 'destructive' }),
  })

  const result = mutation.data?.data

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="flex-1">
          <Label>Host</Label>
          <Input
            placeholder="example.com"
            value={host}
            onChange={(e) => setHost(e.target.value)}
          />
        </div>
        <div className="w-28">
          <Label>Port</Label>
          <Input
            type="number"
            min={1}
            max={65535}
            value={port}
            onChange={(e) => setPort(e.target.value)}
          />
        </div>
        <div className="flex items-end">
          <Button
            onClick={() => mutation.mutate({ host, port: parseInt(port) })}
            disabled={!host || !port || mutation.isPending}
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Server className="h-4 w-4" />}
            <span className="ml-2">Check</span>
          </Button>
        </div>
      </div>

      {result && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              {result.open ? (
                <div className="flex items-center gap-2 text-green-500">
                  <CheckCircle2 className="h-8 w-8" />
                  <div>
                    <p className="font-bold text-lg">Port {result.port} is OPEN</p>
                    <p className="text-sm text-muted-foreground">{result.host} - {result.responseTime}ms</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-500">
                  <XCircle className="h-8 w-8" />
                  <div>
                    <p className="font-bold text-lg">Port {result.port} is CLOSED</p>
                    <p className="text-sm text-muted-foreground">{result.host} - {result.error || 'Connection refused'}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ── Subnet Calculator Tool ────────────────────────────────────────

function SubnetCalcTool() {
  const [cidr, setCidr] = useState('')
  const { toast } = useToast()

  const mutation = useMutation({
    mutationFn: (data: { cidr: string }) =>
      apiClient.post<SubnetCalcResult>('/it-tools/subnet-calc', data),
    onError: () => toast({ title: 'Error', description: 'Subnet calculation failed', variant: 'destructive' }),
  })

  const result = mutation.data?.data

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="flex-1">
          <Label>CIDR Notation</Label>
          <Input
            placeholder="192.168.1.0/24"
            value={cidr}
            onChange={(e) => setCidr(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && cidr && mutation.mutate({ cidr })}
          />
        </div>
        <div className="flex items-end">
          <Button
            onClick={() => mutation.mutate({ cidr })}
            disabled={!cidr || mutation.isPending}
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Network className="h-4 w-4" />}
            <span className="ml-2">Calculate</span>
          </Button>
        </div>
      </div>

      {result && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">{result.cidr}</CardTitle>
              <Badge variant={result.isPrivate ? 'secondary' : 'outline'}>
                {result.isPrivate ? 'Private' : 'Public'}
              </Badge>
              <Badge variant="outline">Class {result.ipClass}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { label: 'Network Address', value: result.networkAddress },
                { label: 'Broadcast Address', value: result.broadcastAddress },
                { label: 'Subnet Mask', value: result.subnetMask },
                { label: 'Wildcard Mask', value: result.wildcardMask },
                { label: 'First Host', value: result.firstHost },
                { label: 'Last Host', value: result.lastHost },
                { label: 'Total Hosts', value: result.totalHosts.toLocaleString() },
                { label: 'Usable Hosts', value: result.usableHosts.toLocaleString() },
                { label: 'Prefix Length', value: `/${result.prefixLength}` },
                { label: 'Binary Mask', value: result.binaryMask },
              ].map(({ label, value }) => (
                <div key={label} className="bg-muted/50 rounded-md px-3 py-2">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-sm font-mono truncate" title={value}>{value}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ── SSL Check Tool ────────────────────────────────────────────────

function SslCheckTool() {
  const [host, setHost] = useState('')
  const { toast } = useToast()

  const mutation = useMutation({
    mutationFn: (data: { host: string }) =>
      apiClient.post<SslCheckResult>('/it-tools/ssl-check', data),
    onError: () => toast({ title: 'Error', description: 'SSL check failed', variant: 'destructive' }),
  })

  const result = mutation.data?.data

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="flex-1">
          <Label>Host</Label>
          <Input
            placeholder="example.com"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && host && mutation.mutate({ host })}
          />
        </div>
        <div className="flex items-end">
          <Button
            onClick={() => mutation.mutate({ host })}
            disabled={!host || mutation.isPending}
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            <span className="ml-2">Check</span>
          </Button>
        </div>
      </div>

      {result && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">{result.host}:{result.port}</CardTitle>
              <Badge variant={result.valid ? 'default' : 'destructive'}>
                {result.valid ? 'Valid' : 'Invalid'}
              </Badge>
              <Badge variant={result.daysRemaining > 30 ? 'outline' : result.daysRemaining > 0 ? 'secondary' : 'destructive'}>
                {result.daysRemaining > 0 ? `${result.daysRemaining} days remaining` : 'Expired'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-muted/50 rounded-md px-3 py-2">
                <p className="text-xs text-muted-foreground">Issuer</p>
                <p className="text-sm font-mono">{result.issuer.O || result.issuer.CN || 'N/A'}</p>
              </div>
              <div className="bg-muted/50 rounded-md px-3 py-2">
                <p className="text-xs text-muted-foreground">Subject</p>
                <p className="text-sm font-mono">{result.subject.CN || 'N/A'}</p>
              </div>
              <div className="bg-muted/50 rounded-md px-3 py-2">
                <p className="text-xs text-muted-foreground">Valid From</p>
                <p className="text-sm font-mono">{new Date(result.validFrom).toLocaleDateString()}</p>
              </div>
              <div className="bg-muted/50 rounded-md px-3 py-2">
                <p className="text-xs text-muted-foreground">Valid To</p>
                <p className="text-sm font-mono">{new Date(result.validTo).toLocaleDateString()}</p>
              </div>
              <div className="bg-muted/50 rounded-md px-3 py-2">
                <p className="text-xs text-muted-foreground">Protocol</p>
                <p className="text-sm font-mono">{result.protocol}</p>
              </div>
              <div className="bg-muted/50 rounded-md px-3 py-2">
                <p className="text-xs text-muted-foreground">Key Size</p>
                <p className="text-sm font-mono">{result.bits} bits</p>
              </div>
            </div>
            {result.sans.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-muted-foreground mb-1">Subject Alternative Names ({result.sans.length})</p>
                <div className="flex flex-wrap gap-1">
                  {result.sans.slice(0, 10).map((san) => (
                    <Badge key={san} variant="outline" className="font-mono text-xs">{san}</Badge>
                  ))}
                  {result.sans.length > 10 && (
                    <Badge variant="secondary">+{result.sans.length - 10} more</Badge>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ── Headers Check Tool ────────────────────────────────────────────

function HeadersCheckTool() {
  const [url, setUrl] = useState('')
  const { toast } = useToast()

  const mutation = useMutation({
    mutationFn: (data: { url: string }) =>
      apiClient.post<HeadersCheckResult>('/it-tools/headers', data),
    onError: () => toast({ title: 'Error', description: 'Headers check failed', variant: 'destructive' }),
  })

  const result = mutation.data?.data

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="flex-1">
          <Label>URL</Label>
          <Input
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && url && mutation.mutate({ url })}
          />
        </div>
        <div className="flex items-end">
          <Button
            onClick={() => mutation.mutate({ url })}
            disabled={!url || mutation.isPending}
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
            <span className="ml-2">Analyze</span>
          </Button>
        </div>
      </div>

      {result && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Security Headers Analysis</CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline">HTTP {result.statusCode}</Badge>
                <Badge variant={result.securityScore >= 70 ? 'default' : result.securityScore >= 40 ? 'secondary' : 'destructive'}>
                  Score: {result.securityScore}%
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {result.findings.map((finding, i) => (
                <div key={i} className="flex items-start gap-2 bg-muted/50 rounded-md px-3 py-2">
                  {finding.status === 'present' && <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />}
                  {finding.status === 'missing' && <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />}
                  {finding.status === 'warning' && <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{finding.header}</p>
                    <p className="text-xs text-muted-foreground truncate">{finding.message}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ── IP Info Tool ──────────────────────────────────────────────────

function IpInfoTool() {
  const [ip, setIp] = useState('')
  const { toast } = useToast()

  const mutation = useMutation({
    mutationFn: (data: { ip: string }) =>
      apiClient.post<IpInfoResult>('/it-tools/ip-info', data),
    onError: () => toast({ title: 'Error', description: 'IP info lookup failed', variant: 'destructive' }),
  })

  const result = mutation.data?.data

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="flex-1">
          <Label>IP Address</Label>
          <Input
            placeholder="8.8.8.8"
            value={ip}
            onChange={(e) => setIp(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && ip && mutation.mutate({ ip })}
          />
        </div>
        <div className="flex items-end">
          <Button
            onClick={() => mutation.mutate({ ip })}
            disabled={!ip || mutation.isPending}
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
            <span className="ml-2">Lookup</span>
          </Button>
        </div>
      </div>

      {result && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">IP Info - {result.ip}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { label: 'IP Address', value: result.ip },
                { label: 'Hostname', value: result.hostname || 'N/A' },
                { label: 'City', value: result.city || 'N/A' },
                { label: 'Region', value: result.region || 'N/A' },
                { label: 'Country', value: result.country || 'N/A' },
                { label: 'Location', value: result.loc || 'N/A' },
                { label: 'Organization', value: result.org || 'N/A' },
                { label: 'Postal Code', value: result.postal || 'N/A' },
                { label: 'Timezone', value: result.timezone || 'N/A' },
              ].map(({ label, value }) => (
                <div key={label} className="bg-muted/50 rounded-md px-3 py-2">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-sm font-mono">{value}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ── Saved Results ─────────────────────────────────────────────────

function SavedResultsPanel() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data, isLoading } = useQuery({
    queryKey: ['it-tools', 'saved'],
    queryFn: () => apiClient.get<SavedResult[]>('/it-tools/saved'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/it-tools/saved/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['it-tools', 'saved'] })
      toast({ title: 'Deleted', description: 'Saved result removed' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' }),
  })

  const saved = data?.data || []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (saved.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Bookmark className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
          <p className="text-lg font-medium text-muted-foreground">No saved results</p>
          <p className="text-sm text-muted-foreground">Use the Save button on any tool result to save it here</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-3">
      {saved.map((item) => (
        <Card key={item.id}>
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline">{item.toolName}</Badge>
                  {item.label && <span className="text-sm font-medium">{item.label}</span>}
                </div>
                <p className="text-xs text-muted-foreground">
                  {new Date(item.createdAt).toLocaleString()}
                </p>
                <pre className="mt-2 text-xs bg-muted/50 rounded-md p-2 max-h-32 overflow-auto">
                  {JSON.stringify(item.result, null, 2)}
                </pre>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive"
                onClick={() => deleteMutation.mutate(item.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────

export function ItToolsPage() {
  const [activeTab, setActiveTab] = useState('dns')
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [saveLabel, setSaveLabel] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const saveMutation = useMutation({
    mutationFn: (data: { toolName: string; input: Record<string, unknown>; result: Record<string, unknown>; label?: string }) =>
      apiClient.post('/it-tools/save', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['it-tools', 'saved'] })
      toast({ title: 'Saved', description: 'Result saved successfully' })
      setSaveDialogOpen(false)
      setSaveLabel('')
    },
    onError: () => toast({ title: 'Error', description: 'Failed to save result', variant: 'destructive' }),
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">IT Tools</h1>
        <p className="text-muted-foreground">Network utilities and calculators for MSP technicians</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:grid-cols-9">
          <TabsTrigger value="dns" className="gap-1">
            <Globe className="h-3.5 w-3.5 hidden sm:block" />DNS
          </TabsTrigger>
          <TabsTrigger value="whois" className="gap-1">
            <FileText className="h-3.5 w-3.5 hidden sm:block" />WHOIS
          </TabsTrigger>
          <TabsTrigger value="ping" className="gap-1">
            <Wifi className="h-3.5 w-3.5 hidden sm:block" />Ping
          </TabsTrigger>
          <TabsTrigger value="port" className="gap-1">
            <Server className="h-3.5 w-3.5 hidden sm:block" />Port
          </TabsTrigger>
          <TabsTrigger value="subnet" className="gap-1">
            <Network className="h-3.5 w-3.5 hidden sm:block" />Subnet
          </TabsTrigger>
          <TabsTrigger value="ssl" className="gap-1">
            <Lock className="h-3.5 w-3.5 hidden sm:block" />SSL
          </TabsTrigger>
          <TabsTrigger value="headers" className="gap-1">
            <Shield className="h-3.5 w-3.5 hidden sm:block" />Headers
          </TabsTrigger>
          <TabsTrigger value="ip" className="gap-1">
            <MapPin className="h-3.5 w-3.5 hidden sm:block" />IP Info
          </TabsTrigger>
          <TabsTrigger value="saved" className="gap-1">
            <Save className="h-3.5 w-3.5 hidden sm:block" />Saved
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="dns"><DnsLookupTool /></TabsContent>
          <TabsContent value="whois"><WhoisTool /></TabsContent>
          <TabsContent value="ping"><PingTool /></TabsContent>
          <TabsContent value="port"><PortCheckTool /></TabsContent>
          <TabsContent value="subnet"><SubnetCalcTool /></TabsContent>
          <TabsContent value="ssl"><SslCheckTool /></TabsContent>
          <TabsContent value="headers"><HeadersCheckTool /></TabsContent>
          <TabsContent value="ip"><IpInfoTool /></TabsContent>
          <TabsContent value="saved"><SavedResultsPanel /></TabsContent>
        </div>
      </Tabs>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Result</DialogTitle>
            <DialogDescription>Save this tool result for future reference.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Label (optional)</Label>
              <Input
                value={saveLabel}
                onChange={(e) => setSaveLabel(e.target.value)}
                placeholder="Give this result a name..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => {
              // This is a placeholder - the save button would be added to each tool
              saveMutation.mutate({ toolName: 'unknown', input: {}, result: {}, label: saveLabel || undefined })
            }}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
